import { Component, signal, computed, inject, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, RouterLink, Router } from '@angular/router';
import { ApiService, showToast } from './api.service';

export interface QuoteItem {
  id?: string;
  material_id?: number | null;
  sku: string;
  title: string;
  desc: string;
  icon?: string;
  qty: number;
  unit: string;
  price: number;
  photo_url?: string | null;
}

@Component({
  selector: 'app-admin-quote-detail',
  standalone: true,
  imports: [CommonModule, FormsModule, RouterLink],
  template: `
    <div class="h-full flex flex-col bg-[#F7FAFC] overflow-hidden relative">
      <!-- Header -->
      <div class="px-5 py-3 shrink-0 flex flex-col xl:flex-row xl:items-center justify-between gap-3">
        <div class="min-w-0">
          <a routerLink="/admin/financials/quotes" class="inline-flex items-center gap-1.5 text-slate-500 hover:text-[#1A6FA6] font-semibold text-xs mb-1 transition-colors w-fit">
            <i class="ti ti-arrow-left"></i> Quotes
          </a>
            @if (isLoading()) {
              <div class="h-6 w-44 bg-slate-100 animate-pulse rounded-md mb-1"></div>
              <div class="h-3 w-28 bg-slate-50 animate-pulse rounded"></div>
            } @else {
              <div class="flex items-center gap-2 mb-0.5">
                <h1 class="text-xl font-semibold text-slate-900 tracking-tight">{{ isNew() ? 'New Quote' : 'Quote Builder' }}</h1>
                <span class="px-2 py-0.5 bg-slate-100 text-slate-600 rounded-md text-[10px] font-semibold uppercase tracking-wider border border-slate-200">{{ quoteStatus() }}</span>
              </div>
              <p class="text-slate-500 text-xs">{{ isNew() ? 'Draft' : quoteNo() }} &middot; {{ customerName() || 'Unnamed Customer' }}</p>
            }
        </div>
          <div class="flex flex-wrap gap-2">
           @if (isSigned()) {
             <div class="px-3 py-2 bg-emerald-50 text-emerald-700 rounded-lg text-xs font-semibold border border-emerald-100 flex items-center gap-2 shadow-sm">
                <i class="ti ti-circle-check text-base"></i> SIGNED & LOCKED
             </div>
           } @else {
            <button [disabled]="isSaving()" (click)="saveQuote()" 
                 class="h-9 px-3 bg-[#1A6FA6] text-white rounded-lg text-xs font-semibold shadow-sm transition-colors hover:bg-[#0C3A5A] flex items-center gap-2 disabled:opacity-50">
              <i class="ti ti-device-floppy text-[16px]"></i> {{ isSaving() ? 'Saving...' : 'Save Quote' }}
            </button>
           }
           <button (click)="generatePdf()" [disabled]="generatingPdf() || isNew()" class="h-9 px-3 bg-white border border-slate-300 text-slate-700 rounded-lg text-xs font-semibold shadow-sm hover:bg-slate-50 transition-colors flex items-center gap-2 disabled:opacity-50">
            @if(generatingPdf()) {
               <i class="ti ti-loader animate-spin text-[16px]"></i> Generating...
            } @else {
               <i class="ti ti-file-type-pdf text-[16px]"></i> Preview PDF
            }
          </button>
           <button (click)="sendQuote()" [disabled]="isSaving() || isSigned()" class="h-9 px-3 bg-[#1A6FA6] text-white rounded-lg text-xs font-semibold shadow-sm hover:bg-[#0C3A5A] transition-colors flex items-center gap-2 disabled:opacity-50">
            <i class="ti ti-send text-[16px]"></i> Send to Customer
          </button>
           @if (!isSigned() && quoteStatus() !== 'Accepted') {
             <button (click)="approveQuoteFromAdmin()" [disabled]="isSaving()" class="h-9 px-3 bg-emerald-600 text-white rounded-lg text-xs font-semibold shadow-sm hover:bg-emerald-700 transition-colors flex items-center gap-2 disabled:opacity-50">
              <i class="ti ti-circle-check text-[16px]"></i> Mark Accepted
            </button>
           }
        </div>
      </div>

      <div class="flex-1 overflow-auto px-5 pb-6">
        <div class="max-w-6xl mx-auto space-y-4">
          
          <!-- Job Details Card -->
          <div class="bg-white rounded-xl shadow-sm p-4 border border-[#E7EEF5]">
            <h2 class="text-sm font-semibold text-slate-900 mb-3 flex items-center gap-2">
              <i class="ti ti-building text-slate-400 text-[16px]"></i> Quote Details
            </h2>
            <div class="grid grid-cols-1 md:grid-cols-4 gap-3">
              <div>
                <label class="block text-[10px] font-bold text-slate-500 uppercase tracking-wider mb-1.5">Customer Name</label>
                <input type="text" [(ngModel)]="customerName" class="w-full h-9 px-3 rounded-lg bg-slate-50 border border-slate-200 focus:bg-white focus:border-[#2B9AD2] focus:ring-1 focus:ring-[#2B9AD2] outline-none transition-all text-slate-900 text-sm font-medium">
              </div>
              <div class="md:col-span-2">
                <label class="block text-[10px] font-bold text-slate-500 uppercase tracking-wider mb-1.5">Job Site Address</label>
                <input type="text" [(ngModel)]="jobAddress" class="w-full h-9 px-3 rounded-lg bg-slate-50 border border-slate-200 focus:bg-white focus:border-[#2B9AD2] focus:ring-1 focus:ring-[#2B9AD2] outline-none transition-all text-slate-900 text-sm font-medium">
              </div>
              <div>
                <label class="block text-[10px] font-bold text-slate-500 uppercase tracking-wider mb-1.5">Valid Until</label>
                <input type="date" [(ngModel)]="validUntil" class="w-full h-9 px-3 rounded-lg bg-slate-50 border border-slate-200 focus:bg-white focus:border-[#2B9AD2] focus:ring-1 focus:ring-[#2B9AD2] outline-none transition-all text-slate-900 text-sm font-medium tracking-wide">
              </div>
              <div>
                <label class="block text-[10px] font-bold text-slate-500 uppercase tracking-wider mb-1.5">Status</label>
                <select [(ngModel)]="quoteStatus" class="w-full h-9 px-3 rounded-lg bg-slate-50 border border-slate-200 focus:bg-white focus:border-[#2B9AD2] outline-none text-sm">
                  <option value="Draft">Draft</option>
                  <option value="Sent">Sent</option>
                  <option value="Accepted">Accepted</option>
                  <option value="Declined">Declined</option>
                </select>
              </div>
              @if (quoteStatus() === 'Declined') {
                <div class="md:col-span-4">
                  <label class="block text-[10px] font-bold text-slate-500 uppercase tracking-wider mb-1.5">Decline Reason</label>
                  <textarea [(ngModel)]="declinedReason" rows="2" class="w-full px-3 py-2 rounded-lg bg-slate-50 border border-slate-200 focus:bg-white focus:border-[#2B9AD2] outline-none text-sm"></textarea>
                </div>
              }
            </div>
          </div>

          <!-- Items and Services Included -->
          <div class="space-y-4">
            <div class="flex items-center justify-between gap-3">
               <h2 class="text-sm font-semibold text-slate-900 flex items-center gap-2 uppercase tracking-wide">Items and Services Included</h2>
               <div class="flex gap-2">
                 <button *ngIf="!isSigned()" (click)="openMaterialPicker()" class="px-3 py-2 bg-[#EAF5FA] border border-[#D5EBF6] rounded-lg text-xs font-semibold text-[#1A6FA6] hover:bg-[#D5EBF6] transition-colors shadow-sm flex items-center gap-2">
                   <i class="ti ti-search"></i> Browse Library
                 </button>
                 <button *ngIf="!isSigned()" (click)="addItem()" class="px-3 py-2 bg-white border border-slate-200 rounded-lg text-xs font-semibold text-slate-600 hover:bg-slate-50 transition-colors shadow-sm flex items-center gap-2">
                   <i class="ti ti-plus"></i> Manual Item
                 </button>
               </div>
            </div>

            <!-- Rich Item Blocks -->
            @for (item of items(); track $index; let i = $index) {
              <div class="bg-white rounded-xl shadow-sm border border-[#E7EEF5] p-3 flex gap-3 relative group border-l-4" [class.border-l-[#2B9AD2]]="item.material_id">
                <!-- Delete Button -->
                <button *ngIf="!isSigned()" (click)="removeItem(i)" class="absolute top-3 right-3 w-7 h-7 rounded-lg bg-red-50 text-red-500 hover:bg-red-100 opacity-0 group-hover:opacity-100 transition-all flex items-center justify-center">
                   <i class="ti ti-trash"></i>
                </button>

                <!-- Icon/SKU Box -->
                <div class="w-16 shrink-0 flex flex-col items-center gap-2 pt-1">
                   <input type="text" [(ngModel)]="item.sku" placeholder="SKU" class="w-full text-center text-[10px] font-bold text-slate-400 uppercase tracking-widest bg-transparent border-b border-dashed border-slate-200 focus:border-[#2B9AD2] outline-none pb-1" [readonly]="isSigned()">
                   @if (item.photo_url) {
                     <img [src]="item.photo_url" class="w-12 h-12 rounded-lg object-cover border border-slate-100 bg-slate-50" alt="Material">
                   } @else {
                   <div class="w-11 h-11 rounded-lg flex items-center justify-center border border-slate-100 bg-slate-50" [class.bg-[#EAF5FA]]="item.material_id">
                      <i class="ti" [class.ti-package]="item.material_id" [class.ti-tool]="!item.material_id" class="text-xl text-slate-400" [class.text-[#2B9AD2]]="item.material_id"></i>
                   </div>
                   }
                   @if (item.material_id) {
                     <span class="text-[9px] font-bold text-[#2B9AD2] uppercase">Library Item</span>
                   }
                </div>

                <!-- Content Body -->
                <div class="flex-1 space-y-2 min-w-0">
                   <div class="flex gap-3 pr-10">
                      <div class="flex-1">
                        <input type="text" [(ngModel)]="item.title" placeholder="Item/Service Title" class="w-full text-sm font-semibold text-slate-900 bg-transparent border-none focus:ring-0 p-0 placeholder:text-slate-300" [readonly]="isSigned()">
                      </div>
                   </div>
                   
                   <textarea [(ngModel)]="item.desc" rows="2" placeholder="Description or bullet points (one per line)..." class="w-full text-xs text-slate-600 bg-slate-50 border border-slate-100 rounded-lg p-2.5 resize-none focus:bg-white focus:border-[#2B9AD2] outline-none transition-colors" [readonly]="isSigned()"></textarea>
                </div>

                <!-- Multipliers -->
                <div class="w-32 shrink-0 flex flex-col items-end gap-2 pr-2 justify-center">
                   <div class="flex items-center gap-1.5 text-xs font-semibold text-slate-500 mb-1">
                      <input type="number" [(ngModel)]="item.qty" class="w-12 h-8 text-center bg-slate-50 border border-slate-200 rounded-lg focus:bg-white focus:border-[#2B9AD2] outline-none px-1.5" min="0.01" [readonly]="isSigned()">
                      <span class="text-xs uppercase">{{item.unit}}</span>
                      <span>@</span>
                   </div>
                   <div class="flex items-center gap-1">
                      <span class="text-slate-400 font-semibold">$</span>
                      <input type="number" [(ngModel)]="item.price" class="w-20 h-8 text-right font-semibold text-slate-900 bg-slate-50 border border-slate-200 rounded-lg focus:bg-white focus:border-[#2B9AD2] outline-none px-2" [readonly]="isSigned()">
                   </div>
                   <div class="mt-2 text-right">
                      <p class="text-[10px] font-bold text-slate-400 uppercase tracking-widest mb-0.5">Line Total</p>
                      <p class="text-base font-semibold text-slate-900">{{ (item.qty * item.price) | currency }}</p>
                   </div>
                </div>
              </div>
            }

            @if (items().length === 0) {
              <div class="py-8 bg-white rounded-xl border border-dashed border-slate-200 flex flex-col items-center justify-center text-slate-400">
                <i class="ti ti-playlist-add text-3xl mb-2 opacity-20"></i>
                <p class="text-sm font-semibold">No items added to this quote yet.</p>
                <p class="text-xs">Click "Manual Item" or "Browse Library" to get started.</p>
              </div>
            }
          </div>

          <!-- Summary -->
          <div class="flex justify-end pt-1">
            <div class="w-full md:w-[380px] bg-white rounded-xl shadow-sm border border-[#E7EEF5] overflow-hidden">
              <div class="p-4">
                <div class="space-y-2.5 text-sm">
                  <div class="flex justify-between text-slate-500 font-medium">
                    <span>Sub-Total (exc. GST)</span>
                    <span class="text-slate-900">{{ subtotal() | currency }}</span>
                  </div>
                  <div class="flex justify-between text-slate-500 font-medium">
                    <span>GST (15%)</span>
                    <span class="text-slate-900">{{ tax() | currency }}</span>
                  </div>
                  <div class="flex justify-between items-center bg-slate-50 p-3 rounded-lg border border-slate-100">
                    <span class="font-semibold text-slate-700">Apply Discount</span>
                    <div class="flex items-center gap-1">
                      <span class="text-red-500 font-semibold">-$</span>
                      <input type="number" [(ngModel)]="globalDiscount" class="w-20 h-8 text-right font-semibold text-red-600 bg-white border border-slate-200 rounded-lg outline-none px-2 focus:border-red-500" [readonly]="isSigned()">
                    </div>
                  </div>
                </div>
              </div>

              <div class="px-4 py-3 bg-[#F1F9FE] border-t border-[#D5EBF6]">
                <div class="flex justify-between items-end">
                  <span class="text-[#1A6FA6] font-semibold tracking-widest uppercase text-[11px] mb-1">Total <span class="text-[10px] normal-case">(inc. GST)</span></span>
                  <span class="text-2xl font-semibold text-slate-950">{{ total() | currency }}</span>
                </div>
                
                @if (globalDiscount() > 0) {
                  <div class="mt-3 pt-3 border-t border-[#D5EBF6] flex items-center justify-between text-emerald-700 font-medium text-xs">
                    <span class="flex items-center gap-2"><i class="ti ti-tag"></i> Discount Applied</span>
                    <span class="font-semibold text-sm text-emerald-700">- {{ globalDiscount() | currency }}</span>
                  </div>
                }
              </div>
            </div>
          </div>

          @if (communications().length > 0) {
            <div class="bg-white rounded-xl shadow-sm p-4 border border-[#E7EEF5]">
              <h2 class="text-sm font-semibold text-slate-900 mb-3 flex items-center gap-2">
                <i class="ti ti-message-2 text-slate-400 text-[16px]"></i> Communication History
              </h2>
              <div class="space-y-2">
                @for (entry of communications(); track entry.id) {
                  <div class="flex gap-3 rounded-lg border border-slate-100 bg-slate-50/70 p-3">
                    <div class="w-8 h-8 rounded-lg bg-white border border-slate-100 text-[#1A6FA6] flex items-center justify-center shrink-0">
                      <i class="ti" [class.ti-send]="entry.event_type === 'sent'" [class.ti-file-type-pdf]="entry.event_type === 'pdf_generated'" [class.ti-pencil]="entry.event_type !== 'sent' && entry.event_type !== 'pdf_generated'"></i>
                    </div>
                    <div class="flex-1">
                      <div class="flex items-center justify-between gap-3">
                        <p class="text-xs font-semibold text-slate-800">{{ communicationLabel(entry.event_type) }}</p>
                        <p class="text-[11px] text-slate-400 font-medium">{{ entry.created_at | date:'dd MMM yyyy, h:mm a' }}</p>
                      </div>
                      <p class="text-xs text-slate-600 mt-0.5">{{ entry.message }}</p>
                      <p class="text-[11px] text-slate-400 mt-1">{{ entry.actor_name || 'System' }} via {{ entry.channel || 'System' }}</p>
                    </div>
                  </div>
                }
              </div>
            </div>
          }
        </div>
      </div>

      <!-- Material Library Picker Modal -->
      @if (showPicker()) {
        <div class="fixed inset-0 bg-slate-900/60 backdrop-blur-sm z-[100] flex items-center justify-center p-4">
          <div class="bg-white rounded-xl shadow-2xl w-full max-w-2xl h-[78vh] flex flex-col overflow-hidden animate-in fade-in zoom-in duration-200">
            <div class="p-4 border-b border-slate-100 flex items-center justify-between bg-slate-50/50">
              <h2 class="text-base font-semibold text-slate-900">Materials Library</h2>
              <button (click)="showPicker.set(false)" class="w-9 h-9 flex items-center justify-center rounded-lg hover:bg-slate-200 text-slate-400 transition-colors">
                <i class="ti ti-x text-lg"></i>
              </button>
            </div>
            
            <div class="p-3 border-b border-slate-100">
              <div class="relative">
                <i class="ti ti-search absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 text-base"></i>
                <input type="text" placeholder="Search by name or SKU..." 
                       [(ngModel)]="searchQuery" (input)="onSearch()"
                       class="w-full h-10 pl-10 pr-3 rounded-lg bg-slate-50 border border-slate-200 outline-none focus:bg-white focus:border-[#2B9AD2] transition-all text-sm">
              </div>
            </div>

            <div class="flex-1 overflow-auto p-2">
              @if (isSearching()) {
                <div class="flex flex-col items-center justify-center h-full text-slate-400">
                  <i class="ti ti-loader animate-spin text-3xl mb-2"></i>
                  <p class="text-sm font-medium">Searching library...</p>
                </div>
              } @else if (libraryResults().length === 0) {
                <div class="flex flex-col items-center justify-center h-full text-slate-400">
                  <i class="ti ti-package-off text-3xl mb-2 opacity-20"></i>
                  <p class="text-sm font-semibold">No materials found.</p>
                </div>
              } @else {
                <div class="grid grid-cols-1 gap-2">
                  @for (m of libraryResults(); track m.id) {
                    <div (click)="selectMaterial(m)" class="flex items-center gap-3 p-3 rounded-lg hover:bg-[#F1F9FE] cursor-pointer group transition-all border border-transparent hover:border-[#D5EBF6]">
                      <div class="w-10 h-10 rounded-lg bg-white border border-slate-100 shadow-sm overflow-hidden shrink-0 flex items-center justify-center">
                        <img [src]="m.photo_url || defaultPhoto" class="w-full h-full object-cover">
                      </div>
                      <div class="flex-1">
                        <p class="text-sm font-semibold text-slate-900 group-hover:text-[#2B9AD2] transition-colors">{{m.name}}</p>
                        <p class="text-xs text-slate-400 font-mono">{{m.serial_no}} &middot; {{m.unit}}</p>
                      </div>
                      <div class="text-right">
                        <p class="text-sm font-semibold text-slate-900">{{m.max_sell_price | currency}}</p>
                        <p class="text-[10px] text-slate-400 uppercase font-semibold">Suggested</p>
                      </div>
                    </div>
                  }
                </div>
              }
            </div>
          </div>
        </div>
      }
    </div>
  `
})
export class AdminQuoteDetailComponent implements OnInit {
  private api = inject(ApiService);
  private route = inject(ActivatedRoute);
  private router = inject(Router);

  quoteId: string | null = null;
  isNew = signal(false);
  isLoading = signal(true);
  isSaving = signal(false);
  generatingPdf = signal(false);
  creatingInvoice = signal(false);

  // Quote Header
  jobId = signal<string | null>(null);
  modelType = signal<'Job' | 'Project'>('Job');
  quoteNo = signal('QT-NEW');
  customerName = signal('');
  jobAddress = signal('');
  quoteStatus = signal('Draft');
  validUntil = signal(new Date(new Date().setMonth(new Date().getMonth() + 1)).toISOString().split('T')[0]);
  globalDiscount = signal(0);
  notes = signal('');
  declinedReason = signal('');
  isSigned = signal(false);

  // Items
  items = signal<QuoteItem[]>([]);
  communications = signal<any[]>([]);

  // Library Picker
  showPicker = signal(false);
  searchQuery = '';
  isSearching = signal(false);
  libraryResults = signal<any[]>([]);
  defaultPhoto = 'https://nzplumbinghq.co.nz/api/assets/default_mat.jpeg';

  ngOnInit() {
    this.quoteId = this.route.snapshot.paramMap.get('id');
    const qParams = this.route.snapshot.queryParamMap;
    const referenceId = qParams.get('project_id') || qParams.get('job_id') || qParams.get('reference_id');
    const requestedModel = qParams.get('model_type') === 'Project' || qParams.get('project_id') ? 'Project' : 'Job';
    this.modelType.set(requestedModel);

    if (this.quoteId === 'new') {
      this.isNew.set(true);
      if (referenceId) {
        this.jobId.set(referenceId);
        this.loadFromReference(referenceId, requestedModel);
      } else {
        this.isLoading.set(false);
      }
    } else if (this.quoteId) {
      this.loadQuote();
    }
  }

  loadFromReference(referenceId: string, modelType: 'Job' | 'Project') {
    this.isLoading.set(true);
    this.api.getQuoteContext(referenceId, modelType).subscribe({
      next: (res: any) => {
        if (res.success) {
          // If a quote already exists for this reference, redirect to it
          if (res.data.existing_quote) {
            this.isSigned.set(!!res.data.existing_quote.signature_data);
            this.router.navigate(['/admin/financials/quotes', res.data.existing_quote.id]);
            return;
          }

          // Pre-populate with job data
          const job = res.data.job;
          const labourMultiplier = job.type === 'Emergency' && Number(job.emergency_double_rate || 0) === 1 ? 2 : 1;
          this.customerName.set(job.customer_name);
          this.jobAddress.set(job.address);
          
          // Map Labour from context
          const lines: QuoteItem[] = [];
          if ((res.data.pricing_type || job.pricing_type) === 'Fixed') {
            const recordedHours = parseFloat(res.data.duration_hours || job.actual_hours || 0);
            lines.push({
              sku: 'LABOUR',
              title: 'Labour: Fixed Rate Job',
              desc: `Fixed-rate labour charge${labourMultiplier === 2 ? ' with the emergency 2× multiplier' : ''}. ${recordedHours.toFixed(2)} actual hour(s) recorded for internal costing; customer billing is not hourly.`,
              qty: 1,
              unit: 'EA',
              price: parseFloat(res.data.fixed_labour_charge || job.fixed_labour_charge || 0) * labourMultiplier,
              photo_url: null
            });
          } else if (res.data.workers) {
            res.data.workers.forEach((w: any) => {
               lines.push({
                 sku: 'LABOUR',
                 title: `Labour: ${w.name}`,
                 desc: w.details || 'Plumbing services',
                 qty: parseFloat(w.duration_hours ?? res.data.duration_hours ?? 0),
                 unit: 'HRS',
                 price: parseFloat(w.hourly_rate || 0) * labourMultiplier,
                 photo_url: null
               });
            });
          }
          // Map Materials from context
          if (res.data.materials) {
            res.data.materials.forEach((m: any) => {
               lines.push({
                 material_id: m.material_id,
                 sku: m.serial_no || 'MAT',
                 title: m.item_name,
                 desc: '',
                 qty: parseFloat(m.quantity),
                 unit: m.unit || 'EA',
                 price: parseFloat(m.sell_price || m.price || 0),
                 photo_url: m.photo_url || null
               });
            });
          }
          this.items.set(lines);
        }
        this.isLoading.set(false);
      },
      error: () => this.isLoading.set(false)
    });
  }

  loadQuote() {
    this.isLoading.set(true);
    this.api.getQuote(this.quoteId!).subscribe({
      next: (res: any) => {
        if (res.success) {
          const q = res.data.quote;
          this.isSigned.set(!!q.signature_data);
          this.quoteNo.set(q.quote_no);
          this.jobId.set(q.reference_id || null);
          this.modelType.set(q.model_type === 'Project' ? 'Project' : 'Job');
          this.customerName.set(q.customer_name);
          this.jobAddress.set(q.job_address);
          this.quoteStatus.set(q.status);
          this.validUntil.set(q.valid_until || this.validUntil());
          this.globalDiscount.set(parseFloat(q.discount || 0));
          this.notes.set(q.notes || '');
          this.declinedReason.set(q.declined_reason || '');
          this.items.set(q.items || []);
          this.communications.set(q.communications || []);
        }
        this.isLoading.set(false);
      },
      error: () => {
        showToast('error', 'Failed to load quote details');
        this.isLoading.set(false);
      }
    });
  }

  subtotal = computed(() => {
    return this.items().reduce((acc, item) => acc + (parseFloat(item.qty as any) * parseFloat(item.price as any)), 0);
  });

  tax = computed(() => this.subtotal() * 0.15);
  
  total = computed(() => {
    const rawTotal = this.subtotal() + this.tax();
    return Math.max(0, rawTotal - this.globalDiscount());
  });

  addItem() {
    this.items.update(curr => [
      ...curr, 
      { sku: '', title: '', desc: '', qty: 1, unit: 'EA', price: 0, photo_url: null }
    ]);
  }

  removeItem(index: number) {
    this.items.update(curr => {
      const copy = [...curr];
      copy.splice(index, 1);
      return copy;
    });
  }

  // --- Material Library ---

  openMaterialPicker() {
    this.showPicker.set(true);
    this.onSearch();
  }

  onSearch() {
    this.isSearching.set(true);
    this.api.getMaterials({ search: this.searchQuery.trim(), limit: 50, include_children: false }).subscribe({
      next: (res: any) => {
        this.libraryResults.set(res.data.materials || []);
        this.isSearching.set(false);
      },
      error: () => this.isSearching.set(false)
    });
  }

  selectMaterial(m: any) {
    this.items.update(curr => [
      ...curr,
      {
        material_id: m.id,
        sku: m.serial_no,
        title: m.name,
        desc: m.description || '',
        qty: 1,
        unit: m.unit,
        price: parseFloat(m.max_sell_price || 0),
        photo_url: m.photo_url || null
      }
    ]);
    this.showPicker.set(false);
    showToast('success', `Added ${m.name} from library`);
  }

  // --- Persistence ---

  saveQuote(sendAfter = false, successMessage = '') {
    if (!this.jobId()) {
      showToast('error', 'Open quote creation from a job or project so it can stay linked to the work record.');
      return;
    }

    this.isSaving.set(true);
    const payload = {
      id: this.quoteId === 'new' ? null : this.quoteId,
      reference_id: this.jobId(),
      model_type: this.modelType(),
      customer_name: this.customerName(),
      job_address: this.jobAddress(),
      status: this.quoteStatus(),
      subtotal: this.subtotal(),
      tax: this.tax(),
      discount: this.globalDiscount(),
      total: this.total(),
      notes: this.notes(),
      declined_reason: this.quoteStatus() === 'Declined' ? this.declinedReason() : null,
      valid_until: this.validUntil(),
      items: this.items().map(it => ({
        material_id: it.material_id,
        sku: it.sku,
        title: it.title,
        desc: it.desc,
        qty: it.qty,
        unit: it.unit,
        price: it.price,
        photo_url: it.photo_url || null
      }))
    };

    const obs = this.isNew() ? this.api.createQuote(payload) : this.api.updateQuote(payload);

    obs.subscribe({
      next: (res: any) => {
        this.isSaving.set(false);
        if (res.success) {
          showToast('success', successMessage || (this.isNew() ? 'Quote created' : 'Quote saved'));
          const savedId = res.data?.id || this.quoteId;
          if (sendAfter && savedId) {
            this.dispatchQuoteEmail(savedId);
          }
          if (this.isNew()) {
            this.router.navigate(['/admin/financials/quotes', res.data.id]);
          }
        } else {
          showToast('error', res.message);
        }
      },
      error: () => {
        this.isSaving.set(false);
        showToast('error', 'Network error while saving');
      }
    });
  }

  sendQuote() {
    if (this.isSigned()) return;
    this.quoteStatus.set('Sent');
    this.saveQuote(true);
  }

  approveQuoteFromAdmin() {
    if (this.isSigned()) return;
    this.quoteStatus.set('Accepted');
    this.declinedReason.set('');
    this.saveQuote(false, 'Quote marked as accepted');
  }

  dispatchQuoteEmail(quoteId: string | number) {
    this.api.sendQuoteToCustomer(quoteId).subscribe({
      next: (res: any) => {
        if (res.success) {
          showToast('success', 'Quote emailed to customer.');
          if (res.data?.approval_url) {
            console.log('Customer quote URL:', res.data.approval_url);
          }
          this.loadQuote();
        } else {
          showToast('error', res.message || 'Quote saved, but email was not sent.');
        }
      },
      error: (err: any) => showToast('error', err?.error?.message || 'Quote saved, but email was not sent.')
    });
  }

  createInvoiceFromQuote() {
    if (!this.quoteId || this.quoteId === 'new') return;
    this.creatingInvoice.set(true);
    this.api.createInvoiceFromQuote(this.quoteId).subscribe({
      next: (res: any) => {
        this.creatingInvoice.set(false);
        if (res.success && res.data?.id) {
          showToast('success', 'Invoice created from accepted quote');
          this.router.navigate(['/admin/financials/invoices', res.data.id]);
        } else {
          showToast('error', res.message || 'Failed to create invoice');
        }
      },
      error: () => {
        this.creatingInvoice.set(false);
        showToast('error', 'Failed to create invoice');
      }
    });
  }

  generatePdf() {
    if (this.isNew() || !this.quoteId) return;
    this.router.navigate(['/admin/financials/pdf', 'quote', this.quoteId]);
  }

  openPdfFromResponse(data: any) {
    if (data?.pdf_base64) {
      const byteCharacters = atob(data.pdf_base64);
      const byteNumbers = new Array(byteCharacters.length);
      for (let i = 0; i < byteCharacters.length; i++) {
        byteNumbers[i] = byteCharacters.charCodeAt(i);
      }
      const blob = new Blob([new Uint8Array(byteNumbers)], { type: 'application/pdf' });
      const url = URL.createObjectURL(blob);
      window.open(url, '_blank');
      setTimeout(() => URL.revokeObjectURL(url), 60000);
      return;
    }
    if (data?.pdf_url) {
      window.open(this.api.baseUrl.replace('/v1', '') + data.pdf_url, '_blank');
    }
  }

  communicationLabel(type: string): string {
    const labels: Record<string, string> = {
      created: 'Quote Created',
      updated: 'Quote Updated',
      sent: 'Sent to Customer',
      accepted: 'Quote Accepted',
      declined: 'Quote Declined',
      signed: 'Signed and Finalized',
      pdf_generated: 'PDF Preview Generated'
    };
    return labels[type] || 'Quote Activity';
  }
}
